import type { Fill, Primitive, TextPrimitive } from '@vp/shared-types';
import { keepsDetail } from '../display';
import { adaptiveGridStep, fmod, type BackendStats, type FrameInput, type RenderBackend } from '../backend';
import { inkRgb, parseColor } from '../color';
import { patternSegments, segmentQuads } from './pattern';
import { patternSpacing, patternThickness } from '../canvas2d';
import { arrowHead, dashKind, trimForArrows } from '../pathgeom';
import { fontString, layoutText, textEpoch, type TextLayout } from '../text';
import type { RenderItem } from '../scene';
import { GRID_FS, GRID_VS, MESH_FS, MESH_VS, SHAPE_FS, SHAPE_VS, TEX_FS, TEX_VS } from './shaders';
import { fillPolygon, roundStroke, strokeStyled, strokeVariable } from './tessellate';

const SHAPE_STRIDE = 26; // static floats per shape instance (center is dynamic)
const MESH_STRIDE = 7; // pos(2) + depth(1) + color(4)
/** Depth step between meshes: a mesh never blends twice over itself (no beads at joins of see-through strokes). */
const MESH_DEPTH_STEP = 1 / (1 << 21);
const TEX_STRIDE = 5; // pos(2) + uv(2) + alpha(1)
const DYN = 6; // dynamic floats per instance/vertex: center(2) + clip rect(4)
const NO_CLIP = 1e30;

type Cmd =
  | { p: 'shape'; start: number; count: number }
  | { p: 'mesh'; start: number; count: number; blend?: 'highlight'; skipInk?: boolean }
  | { p: 'tex'; start: number; count: number; tex: WebGLTexture; image?: boolean };

type MeshLook = { blend?: 'highlight'; skipInk?: boolean };

function meshSame(a: Cmd | null, look: MeshLook): boolean {
  return !!a && a.p === 'mesh' && a.blend === look.blend && a.skipInk === look.skipInk;
}

interface TexEntry {
  tex: WebGLTexture;
  bytes: number;
  lastUsed: number;
  w: number;
  h: number;
}

function createProgram(gl: WebGL2RenderingContext, vs: string, fs: string): WebGLProgram {
  const compile = (type: number, src: string) => {
    const sh = gl.createShader(type)!;
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS) && !gl.isContextLost()) {
      throw new Error('Shader compile error: ' + gl.getShaderInfoLog(sh));
    }
    return sh;
  };
  const p = gl.createProgram()!;
  gl.attachShader(p, compile(gl.VERTEX_SHADER, vs));
  gl.attachShader(p, compile(gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS) && !gl.isContextLost()) {
    throw new Error('Program link error: ' + gl.getProgramInfoLog(p));
  }
  return p;
}

class GrowBuffer {
  data: Float32Array;
  len = 0;
  constructor(initial = 4096) {
    this.data = new Float32Array(initial);
  }
  reserve(n: number): void {
    if (this.len + n <= this.data.length) return;
    let cap = this.data.length;
    while (cap < this.len + n) cap *= 2;
    const next = new Float32Array(cap);
    next.set(this.data.subarray(0, this.len));
    this.data = next;
  }
  reset(): void {
    this.len = 0;
  }
}

class GrowIndex {
  data: Uint32Array;
  len = 0;
  constructor(initial = 1024) {
    this.data = new Uint32Array(initial);
  }
  push(v: number, n: number): void {
    if (this.len + n > this.data.length) {
      let cap = this.data.length;
      while (cap < this.len + n) cap *= 2;
      const next = new Uint32Array(cap);
      next.set(this.data.subarray(0, this.len));
      this.data = next;
    }
    this.data.fill(v, this.len, this.len + n);
    this.len += n;
  }
  reset(): void {
    this.len = 0;
  }
}

/**
 * WebGL 2 backend (§11.2): instanced SDF shapes, CPU-tessellated paths, text via
 * Canvas 2D → texture. Static per-item data is rebuilt only when the item list
 * changes; per frame only view-relative centers are updated.
 */
export class WebGL2Backend implements RenderBackend {
  readonly kind = 'webgl2' as const;
  lost = false;
  private gl: WebGL2RenderingContext;
  private shapeProg: WebGLProgram;
  private meshProg: WebGLProgram;
  private texProg: WebGLProgram;
  private gridProg: WebGLProgram;
  private quadBuf: WebGLBuffer;
  private shapeStatic: WebGLBuffer;
  private shapeDyn: WebGLBuffer;
  private meshStatic: WebGLBuffer;
  private meshDyn: WebGLBuffer;
  private texStatic: WebGLBuffer;
  private texDyn: WebGLBuffer;
  private shapeVao: WebGLVertexArrayObject;
  private meshVao: WebGLVertexArrayObject;
  private texVao: WebGLVertexArrayObject;
  private gridVao: WebGLVertexArrayObject;
  private uniforms = new Map<WebGLProgram, Map<string, WebGLUniformLocation | null>>();

  private shapes = new GrowBuffer(SHAPE_STRIDE * 1024);
  private mesh = new GrowBuffer(MESH_STRIDE * 4096);
  private texv = new GrowBuffer(TEX_STRIDE * 1024);
  private shapeOwner = new GrowIndex();
  private meshOwner = new GrowIndex();
  private texOwner = new GrowIndex();
  private dyn = new Float32Array(2048);
  private cmds: Cmd[] = [];

  private lastItems: RenderItem[] | null = null;
  private lastSig = '';
  private lastBuildScale = 0;
  private staticDirty = true;

  private textures = new Map<string, TexEntry>();
  private texBytes = 0;
  private texBudget = 192 * 1024 * 1024;
  private frameNo = 0;
  private uploadsLeft = 0;
  private pendingCount = 0;
  private drawCalls = 0;
  private maxTex: number;
  private textCanvas: OffscreenCanvas | HTMLCanvasElement | null = null;
  /** Text too big for one texture at this zoom is drawn as viewport tiles; the world area they cover. */
  private tiledText = false;
  private dark = false;
  private meshDepth = 1;
  private tileView: { x0: number; y0: number; x1: number; y1: number } | null = null;

  constructor(private readonly canvas: HTMLCanvasElement | OffscreenCanvas) {
    const gl = canvas.getContext('webgl2', {
      antialias: true,
      alpha: false,
      premultipliedAlpha: true,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    }) as WebGL2RenderingContext | null;
    if (!gl) throw new Error('WebGL 2 is not available');
    this.gl = gl;
    if ('addEventListener' in canvas) {
      canvas.addEventListener('webglcontextlost', (e) => {
        e.preventDefault();
        this.lost = true;
      });
    }
    this.maxTex = Math.min(4096, gl.getParameter(gl.MAX_TEXTURE_SIZE) as number);
    this.shapeProg = createProgram(gl, SHAPE_VS, SHAPE_FS);
    this.meshProg = createProgram(gl, MESH_VS, MESH_FS);
    this.texProg = createProgram(gl, TEX_VS, TEX_FS);
    this.gridProg = createProgram(gl, GRID_VS, GRID_FS);

    this.quadBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, 1, 1, -1, -1, 1, 1, -1, 1]), gl.STATIC_DRAW);
    this.shapeStatic = gl.createBuffer()!;
    this.shapeDyn = gl.createBuffer()!;
    this.meshStatic = gl.createBuffer()!;
    this.meshDyn = gl.createBuffer()!;
    this.texStatic = gl.createBuffer()!;
    this.texDyn = gl.createBuffer()!;
    this.shapeVao = gl.createVertexArray()!;
    this.meshVao = gl.createVertexArray()!;
    this.texVao = gl.createVertexArray()!;
    this.gridVao = gl.createVertexArray()!;
    gl.bindVertexArray(this.shapeVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 8, 0);
    gl.bindVertexArray(null);
  }

  private u(p: WebGLProgram, name: string): WebGLUniformLocation | null {
    let m = this.uniforms.get(p);
    if (!m) this.uniforms.set(p, (m = new Map()));
    if (!m.has(name)) m.set(name, this.gl.getUniformLocation(p, name));
    return m.get(name)!;
  }

  resize(width: number, height: number, dpr: number): void {
    this.canvas.width = Math.max(1, Math.round(width * dpr));
    this.canvas.height = Math.max(1, Math.round(height * dpr));
  }

  stats(): BackendStats {
    return {
      drawCalls: this.drawCalls,
      instances: this.shapes.len / SHAPE_STRIDE + this.mesh.len / MESH_STRIDE / 3 + this.texv.len / TEX_STRIDE / 6,
      textures: this.textures.size,
      textureBytes: this.texBytes,
      pending: this.pendingCount,
    };
  }

  dispose(): void {
    const gl = this.gl;
    for (const t of this.textures.values()) gl.deleteTexture(t.tex);
    this.textures.clear();
    for (const b of [this.quadBuf, this.shapeStatic, this.shapeDyn, this.meshStatic, this.meshDyn, this.texStatic, this.texDyn]) gl.deleteBuffer(b);
    for (const p of [this.shapeProg, this.meshProg, this.texProg, this.gridProg]) gl.deleteProgram(p);
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  }

  render(frame: FrameInput): void {
    const gl = this.gl;
    if (gl.isContextLost()) {
      this.lost = true;
      return;
    }
    this.frameNo++;
    this.drawCalls = 0;
    this.uploadsLeft = 12;
    const cam = frame.camera;
    const W = this.canvas.width;
    const H = this.canvas.height;
    const scale = cam.zoom * cam.dpr;
    gl.viewport(0, 0, W, H);
    this.dark = !!frame.darkInk;
    const bg0 = parseColor(frame.background);
    const bg = this.dark ? inkRgb(bg0[0], bg0[1], bg0[2]) : bg0;
    gl.clearColor(bg[0], bg[1], bg[2], 1);
    gl.clearDepth(1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.depthFunc(gl.LESS);
    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    if (frame.grid?.enabled) this.drawGrid(frame, W, H, scale);

    // tiled text: rebuild once the view leaves the area its tiles were made for
    if (this.tiledText && this.tileView) {
      const v = viewRect(cam, 0);
      const t = this.tileView;
      if (v.x0 < t.x0 || v.y0 < t.y0 || v.x1 > t.x1 || v.y1 > t.y1) this.staticDirty = true;
    }
    // static rebuild when items / revisions / scale bucket change or a pending texture became ready
    const scaleChanged = this.lastBuildScale === 0 || Math.abs(Math.log2(scale / this.lastBuildScale)) > 0.5;
    if (frame.items !== this.lastItems || this.staticDirty || scaleChanged || this.pendingCount > 0) {
      const sig = signature(frame.items);
      if (sig !== this.lastSig || this.staticDirty || scaleChanged || this.pendingCount > 0) {
        this.buildStatic(frame, scale);
        this.lastSig = sig;
        this.lastBuildScale = scale;
      }
      this.lastItems = frame.items;
      this.staticDirty = false;
    }

    // dynamic: view-relative centers (float64 → small float32)
    const items = frame.items;
    // per item: center (2) + frame clip rect x0,y0,x1,y1 (4), all relative to the camera
    const per = new Float64Array(items.length * DYN);
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const o = it.obj;
      const k = i * DYN;
      per[k] = o.x + o.w / 2 - cam.cx;
      per[k + 1] = o.y + o.h / 2 - cam.cy;
      const c = it.clip;
      if (c) {
        per[k + 2] = c.x - cam.cx;
        per[k + 3] = c.y - cam.cy;
        per[k + 4] = c.x + c.w - cam.cx;
        per[k + 5] = c.y + c.h - cam.cy;
      } else {
        per[k + 2] = -NO_CLIP;
        per[k + 3] = -NO_CLIP;
        per[k + 4] = NO_CLIP;
        per[k + 5] = NO_CLIP;
      }
    }
    const uploadDyn = (owner: GrowIndex, buf: WebGLBuffer) => {
      const n = owner.len;
      if (this.dyn.length < n * DYN) this.dyn = new Float32Array(Math.max(n * DYN, this.dyn.length * 2));
      const d = this.dyn;
      const od = owner.data;
      for (let i = 0; i < n; i++) {
        const src = od[i] * DYN;
        const dst = i * DYN;
        for (let j = 0; j < DYN; j++) d[dst + j] = per[src + j];
      }
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, d.subarray(0, n * DYN), gl.STREAM_DRAW);
    };
    uploadDyn(this.shapeOwner, this.shapeDyn);
    uploadDyn(this.meshOwner, this.meshDyn);
    uploadDyn(this.texOwner, this.texDyn);

    // draw commands in layer order
    let highlight = false;
    const setHighlight = (on: boolean) => {
      if (on === highlight) return;
      highlight = on;
      // darken on paper, lighten on the dark canvas: idempotent, so a stroke never builds up over itself
      gl.blendEquation(on ? (this.dark ? gl.MAX : gl.MIN) : gl.FUNC_ADD);
    };
    for (const c of this.cmds) {
      if (c.p === 'mesh') {
        setHighlight(c.blend === 'highlight');
        gl.enable(gl.DEPTH_TEST);
        this.drawMesh(c.start, c.count, W, H, scale, !c.skipInk, c.blend === 'highlight' ? (this.dark ? 2 : 1) : 0);
        gl.disable(gl.DEPTH_TEST);
      } else {
        setHighlight(false);
        if (c.p === 'shape') this.drawShapes(c.start, c.count, W, H, scale);
        else this.drawTex(c.start, c.count, c.tex, W, H, scale, !c.image);
      }
    }
    setHighlight(false);
    this.evictTextures();
  }

  private drawGrid(frame: FrameInput, W: number, H: number, scale: number): void {
    const gl = this.gl;
    const g = frame.grid!;
    const cam = frame.camera;
    const step = adaptiveGridStep(g.step, cam.zoom, g.kind === 'dots' ? 16 : 8);
    const major = step * 5;
    const left = cam.cx - cam.width / 2 / cam.zoom;
    const top = cam.cy - cam.height / 2 / cam.zoom;
    const c0 = parseColor(g.color);
    const c = this.dark ? [...inkRgb(c0[0], c0[1], c0[2]), c0[3]] : c0;
    gl.useProgram(this.gridProg);
    gl.bindVertexArray(this.gridVao);
    gl.uniform2f(this.u(this.gridProg, 'u_viewport'), W, H);
    gl.uniform1f(this.u(this.gridProg, 'u_scale'), scale);
    gl.uniform2f(this.u(this.gridProg, 'u_origin'), fmod(left, major), fmod(top, major));
    gl.uniform1f(this.u(this.gridProg, 'u_step'), step);
    gl.uniform4f(this.u(this.gridProg, 'u_color'), c[0] * c[3], c[1] * c[3], c[2] * c[3], c[3]);
    gl.uniform1i(this.u(this.gridProg, 'u_kind'), g.kind === 'dots' ? 0 : g.kind === 'lines' ? 1 : 2);
    gl.uniform1f(this.u(this.gridProg, 'u_dpr'), cam.dpr);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    this.drawCalls++;
  }

  private drawShapes(start: number, count: number, W: number, H: number, scale: number): void {
    const gl = this.gl;
    gl.useProgram(this.shapeProg);
    gl.uniform1f(this.u(this.shapeProg, 'u_dark'), this.dark ? 1 : 0);
    gl.bindVertexArray(this.shapeVao);
    gl.uniform2f(this.u(this.shapeProg, 'u_viewport'), W, H);
    gl.uniform1f(this.u(this.shapeProg, 'u_scale'), scale);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.shapeDyn);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, DYN * 4, start * DYN * 4);
    gl.vertexAttribDivisor(1, 1);
    gl.enableVertexAttribArray(9);
    gl.vertexAttribPointer(9, 4, gl.FLOAT, false, DYN * 4, start * DYN * 4 + 8);
    gl.vertexAttribDivisor(9, 1);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.shapeStatic);
    const stride = SHAPE_STRIDE * 4;
    const base = start * stride;
    const attrs: [number, number, number][] = [
      [2, 4, 0],
      [3, 4, 4],
      [4, 4, 8],
      [5, 4, 12],
      [6, 4, 16],
      [7, 4, 20],
      [8, 2, 24],
    ];
    for (const [loc, size, off] of attrs) {
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, size, gl.FLOAT, false, stride, base + off * 4);
      gl.vertexAttribDivisor(loc, 1);
    }
    gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, count);
    this.drawCalls++;
  }

  private drawMesh(start: number, count: number, W: number, H: number, scale: number, ink = true, hl = 0): void {
    const gl = this.gl;
    gl.useProgram(this.meshProg);
    gl.uniform1f(this.u(this.meshProg, 'u_dark'), this.dark && ink ? 1 : 0);
    gl.uniform1f(this.u(this.meshProg, 'u_hl'), hl);
    gl.bindVertexArray(this.meshVao);
    gl.uniform2f(this.u(this.meshProg, 'u_viewport'), W, H);
    gl.uniform1f(this.u(this.meshProg, 'u_scale'), scale);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.meshStatic);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, MESH_STRIDE * 4, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 4, gl.FLOAT, false, MESH_STRIDE * 4, 12);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.meshDyn);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 2, gl.FLOAT, false, DYN * 4, 0);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.FLOAT, false, DYN * 4, 8);
    gl.drawArrays(gl.TRIANGLES, start, count);
    this.drawCalls++;
  }

  private drawTex(start: number, count: number, tex: WebGLTexture, W: number, H: number, scale: number, ink: boolean): void {
    const gl = this.gl;
    gl.useProgram(this.texProg);
    gl.uniform1f(this.u(this.texProg, 'u_dark'), this.dark && ink ? 1 : 0);
    gl.bindVertexArray(this.texVao);
    gl.uniform2f(this.u(this.texProg, 'u_viewport'), W, H);
    gl.uniform1f(this.u(this.texProg, 'u_scale'), scale);
    gl.uniform1i(this.u(this.texProg, 'u_tex'), 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.texStatic);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, TEX_STRIDE * 4, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 3, gl.FLOAT, false, TEX_STRIDE * 4, 8);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.texDyn);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 2, gl.FLOAT, false, DYN * 4, 0);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.FLOAT, false, DYN * 4, 8);
    gl.drawArrays(gl.TRIANGLES, start, count);
    this.drawCalls++;
  }

  // ---------------- static build ----------------

  private buildStatic(frame: FrameInput, scale: number): void {
    const gl = this.gl;
    this.shapes.reset();
    this.mesh.reset();
    this.meshDepth = 1 - MESH_DEPTH_STEP;
    this.texv.reset();
    this.shapeOwner.reset();
    this.meshOwner.reset();
    this.texOwner.reset();
    this.cmds = [];
    this.pendingCount = 0;
    this.tiledText = false;
    this.tileView = viewRect(frame.camera, 0.25);
    const items = frame.items;
    let cur: Cmd | null = null;
    const begin = (p: 'shape' | 'mesh', look: MeshLook = {}): void => {
      if (p === 'shape' && cur?.p === 'shape') return;
      if (p === 'mesh' && meshSame(cur, look)) return;
      if (cur) this.cmds.push(cur);
      cur = p === 'shape' ? { p, start: this.shapes.len / SHAPE_STRIDE, count: 0 } : { p, start: this.mesh.len / MESH_STRIDE, count: 0, ...look };
    };
    const tmp: number[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const o = item.obj;
      const rot = o.rotation;
      const c = Math.cos(rot);
      const s = Math.sin(rot);
      const hw = o.w / 2;
      const hh = o.h / 2;
      const objAlpha = o.style.opacity ?? 1;
      // local (object, top-left origin) → rotated offset from object center
      const tx = (x: number, y: number): [number, number] => {
        const lx = x - hw;
        const ly = y - hh;
        return [lx * c - ly * s, lx * s + ly * c];
      };
      for (let pi = 0; pi < item.prims.length; pi++) {
        const p = item.prims[pi];
        const alpha = objAlpha * (p.opacity ?? 1);
        if (p.k === 'shape') {
          begin('shape');
          this.pushShape(p, tx, rot, alpha);
          this.shapeOwner.push(i, 1);
          cur!.count++;
        } else if (p.k === 'path') {
          const look: MeshLook = p.blend === 'highlight' ? { blend: 'highlight', skipInk: true } : {};
          begin('mesh', look);
          const before = this.mesh.len;
          this.pushPath(p, tx, alpha, scale, tmp);
          const verts = (this.mesh.len - before) / MESH_STRIDE;
          this.meshOwner.push(i, verts);
          cur!.count += verts;
        } else if (p.k === 'text') {
          if (!keepsDetail(p) && p.style.fontSize * scale < frame.textLodPx) {
            begin('mesh');
            const before = this.mesh.len;
            this.pushGreek(p, tx, alpha);
            const verts = (this.mesh.len - before) / MESH_STRIDE;
            this.meshOwner.push(i, verts);
            cur!.count += verts;
            continue;
          }
          const tiles = this.textTiles(frame.camera, item, pi, p, scale);
          if (tiles) {
            if (cur) this.cmds.push(cur);
            cur = null;
            for (const t of tiles) {
              const start = this.texv.len / TEX_STRIDE;
              this.pushQuad(t.x, t.y, t.w, t.h, tx, alpha);
              this.texOwner.push(i, 6);
              this.cmds.push({ p: 'tex', start, count: 6, tex: t.entry.tex });
            }
            continue;
          }
          const tex = this.textTexture(item, pi, p, scale);
          if (!tex) continue;
          if (cur) this.cmds.push(cur);
          cur = null;
          const start = this.texv.len / TEX_STRIDE;
          this.pushQuad(p.x, p.y + tex.y0, p.w, tex.h, tx, alpha);
          this.texOwner.push(i, 6);
          this.cmds.push({ p: 'tex', start, count: 6, tex: tex.entry.tex });
        } else if (p.k === 'image' || p.k === 'raster') {
          const entry = p.k === 'image' ? this.imageTexture(frame, p.src) : this.rasterTexture(p.key, p.bitmap);
          if (!entry) {
            begin('mesh');
            const before = this.mesh.len;
            this.pushRect(p.x, p.y, p.w, p.h, tx, [0.9, 0.91, 0.92, 1], alpha);
            const verts = (this.mesh.len - before) / MESH_STRIDE;
            this.meshOwner.push(i, verts);
            cur!.count += verts;
            continue;
          }
          if (cur) this.cmds.push(cur);
          cur = null;
          const start = this.texv.len / TEX_STRIDE;
          this.pushQuad(p.x, p.y, p.w, p.h, tx, alpha);
          this.texOwner.push(i, 6);
          this.cmds.push({ p: 'tex', start, count: 6, tex: entry.tex, image: p.k === 'image' });
        }
      }
    }
    if (cur) this.cmds.push(cur);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.shapeStatic);
    gl.bufferData(gl.ARRAY_BUFFER, this.shapes.data.subarray(0, this.shapes.len), gl.DYNAMIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.meshStatic);
    gl.bufferData(gl.ARRAY_BUFFER, this.mesh.data.subarray(0, this.mesh.len), gl.DYNAMIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.texStatic);
    gl.bufferData(gl.ARRAY_BUFFER, this.texv.data.subarray(0, this.texv.len), gl.DYNAMIC_DRAW);
  }

  private pushShape(
    p: Extract<Primitive, { k: 'shape' }>,
    tx: (x: number, y: number) => [number, number],
    rot: number,
    alpha: number,
  ): void {
    const b = this.shapes;
    b.reserve(SHAPE_STRIDE);
    const d = b.data;
    let k = b.len;
    const [ox, oy] = tx(p.x + p.w / 2, p.y + p.h / 2);
    d[k++] = ox;
    d[k++] = oy;
    d[k++] = p.w / 2;
    d[k++] = p.h / 2;
    d[k++] = rot;
    d[k++] = p.shape === 'rect' ? 0 : p.shape === 'ellipse' ? 1 : p.shape === 'triangle' ? 2 : 3;
    d[k++] = p.radius ?? 0;
    d[k++] = p.stroke && p.stroke.width > 0 ? p.stroke.width : 0;
    const fill = p.fill;
    const fc = fill.kind === 'none' ? null : parseColor(fill.color);
    k = putColor(d, k, fc, alpha);
    const bgc = (fill.kind === 'lines' || fill.kind === 'grid') && fill.background ? parseColor(fill.background) : null;
    k = putColor(d, k, bgc, alpha);
    k = putColor(d, k, p.stroke ? parseColor(p.stroke.color) : null, alpha);
    d[k++] = fillKindCode(fill);
    d[k++] = p.stroke ? dashKind(p.stroke.dash) : 0;
    d[k++] = patternSpacing(fill);
    d[k++] = fill.kind === 'lines' ? fill.angle ?? Math.PI / 4 : 0;
    d[k++] = p.x + p.w / 2;
    d[k++] = p.y + p.h / 2;
    b.len = k;
  }

  private pushTris(tris: number[], tx: (x: number, y: number) => [number, number], col: readonly number[], alpha: number): void {
    const n = tris.length / 2;
    const b = this.mesh;
    b.reserve(n * MESH_STRIDE);
    const d = b.data;
    let k = b.len;
    const a = col[3] * alpha;
    const z = this.meshDepth;
    this.meshDepth = Math.max(0, z - MESH_DEPTH_STEP);
    for (let i = 0; i < n; i++) {
      const [x, y] = tx(tris[i * 2], tris[i * 2 + 1]);
      d[k++] = x;
      d[k++] = y;
      d[k++] = z;
      d[k++] = col[0] * a;
      d[k++] = col[1] * a;
      d[k++] = col[2] * a;
      d[k++] = a;
    }
    b.len = k;
  }

  private pushRect(x: number, y: number, w: number, h: number, tx: (x: number, y: number) => [number, number], col: readonly number[], alpha: number): void {
    this.pushTris([x, y, x + w, y, x + w, y + h, x, y, x + w, y + h, x, y + h], tx, col, alpha);
  }

  private pushPath(p: Extract<Primitive, { k: 'path' }>, tx: (x: number, y: number) => [number, number], alpha: number, scale: number, tmp: number[]): void {
    if (p.closed && p.fill.kind !== 'none') {
      const rings = [p.points, ...(p.holes ?? [])];
      const fill = p.fill;
      const poly = (col: readonly number[], a: number) => {
        tmp.length = 0;
        fillPolygon(p.points, tmp, p.holes);
        this.pushTris(tmp, tx, col, a);
      };
      if (fill.kind === 'solid') poly(parseColor(fill.color), alpha);
      else {
        // a pattern: its background (if any) as a plain fill, the lines cut to the outline
        const sp = patternSpacing(fill);
        const th = patternThickness(sp);
        const lineCol = parseColor(fill.color);
        if (fill.background) poly(parseColor(fill.background), alpha);
        if (sp * scale < 4) {
          // too dense to resolve on screen: the average tone instead of moire (as in Canvas 2D)
          const density = Math.min(1, fill.kind === 'grid' ? (2 * th) / sp : th / sp);
          poly([lineCol[0], lineCol[1], lineCol[2], lineCol[3] * density], alpha);
        } else {
          const segs = patternSegments(rings, fill, sp);
          const quads: number[] = [];
          segmentQuads(segs, Math.max(th, 0.75 / scale), quads);
          this.pushTris(quads, tx, lineCol, alpha);
        }
      }
    }
    if (p.stroke && p.stroke.width > 0) {
      const minW = 1 / scale;
      const vary = p.widths && p.widths.length === p.points.length / 2;
      const w = Math.max(p.stroke.width, minW);
      const thin = !vary && p.stroke.width < minW ? p.stroke.width / minW : 1;
      const col = parseColor(p.stroke.color);
      // a closed path outlines its holes too
      const rings = p.closed ? [p.points, ...(p.holes ?? [])] : [trimForArrows(p.points, !!p.arrowStart, !!p.arrowEnd, p.stroke.width)];
      tmp.length = 0;
      for (const pts of rings) {
        if (vary && pts === p.points) {
          const ws = p.widths!.map((x) => Math.max(x, minW));
          strokeVariable(pts, ws, p.closed, tmp);
          if (p.round && p.stroke.dash === 'solid') roundStroke(pts, ws, tmp, scale);
        } else {
          strokeStyled(pts, w, p.stroke.dash, p.closed, tmp);
          if (p.round && p.stroke.dash === 'solid') roundStroke(pts, w, tmp, scale);
        }
      }
      if (!p.closed) {
        if (p.arrowEnd) {
          const h = arrowHead(p.points, true, p.stroke.width);
          if (h) tmp.push(...h.tri);
        }
        if (p.arrowStart) {
          const h = arrowHead(p.points, false, p.stroke.width);
          if (h) tmp.push(...h.tri);
        }
      }
      this.pushTris(tmp, tx, col, alpha * thin);
    }
  }

  private pushGreek(p: TextPrimitive, tx: (x: number, y: number) => [number, number], alpha: number): void {
    const layout = layoutText(p.text, p.style, p.w, p.h);
    const col = parseColor(p.style.color);
    const bh = p.style.fontSize * 0.5;
    const tris: number[] = [];
    for (const l of layout.lines) {
      if (l.width <= 0) continue;
      const x = p.x + l.x;
      const y = p.y + l.y - bh;
      tris.push(x, y, x + l.width, y, x + l.width, y + bh, x, y, x + l.width, y + bh, x, y + bh);
    }
    this.pushTris(tris, tx, [col[0], col[1], col[2], col[3] * 0.35], alpha);
  }

  private pushQuad(x: number, y: number, w: number, h: number, tx: (x: number, y: number) => [number, number], alpha: number): void {
    const b = this.texv;
    b.reserve(6 * TEX_STRIDE);
    const d = b.data;
    let k = b.len;
    const corners: [number, number, number, number][] = [
      [x, y, 0, 0],
      [x + w, y, 1, 0],
      [x + w, y + h, 1, 1],
      [x, y, 0, 0],
      [x + w, y + h, 1, 1],
      [x, y + h, 0, 1],
    ];
    for (const [cx, cy, u, v] of corners) {
      const [px, py] = tx(cx, cy);
      d[k++] = px;
      d[k++] = py;
      d[k++] = u;
      d[k++] = v;
      d[k++] = alpha;
    }
    b.len = k;
  }

  // ---------------- textures ----------------

  private uploadTexture(source: TexImageSource, premultiply: boolean): WebGLTexture {
    const gl = this.gl;
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, premultiply);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    return tex;
  }

  private addTexture(key: string, tex: WebGLTexture, w: number, h: number): TexEntry {
    const e: TexEntry = { tex, bytes: w * h * 4, lastUsed: this.frameNo, w, h };
    this.textures.set(key, e);
    this.texBytes += e.bytes;
    return e;
  }

  private textTexture(item: RenderItem, pi: number, p: TextPrimitive, scale: number): { entry: TexEntry; y0: number; h: number } | null {
    const layout = layoutText(p.text, p.style, p.w, p.h);
    const fs = p.style.fontSize;
    const first = layout.lines[0];
    const last = layout.lines[layout.lines.length - 1];
    // vertical extent covers overflow above/below the box
    const y0 = Math.min(0, first ? first.y - fs : 0);
    const y1 = Math.max(p.h, last ? last.y + fs * 0.35 : p.h);
    const h = y1 - y0;
    const w = p.w;
    // power-of-two scale bucket, capped by max texture size
    let bucket = Math.pow(2, Math.ceil(Math.log2(Math.max(scale, 1e-6))));
    while ((w * bucket > this.maxTex || h * bucket > this.maxTex) && bucket > 1e-6) bucket /= 2;
    const base = 't:' + textKey(p) + ':';
    const key = base + bucket;
    const hit = this.textures.get(key);
    if (hit) {
      hit.lastUsed = this.frameNo;
      return { entry: hit, y0, h };
    }
    if (this.uploadsLeft > 0) {
      this.uploadsLeft--;
      const tex = this.renderTextTexture(p, layout, w, y0, h, bucket);
      if (tex) return { entry: this.addTexture(key, tex.tex, tex.pw, tex.ph), y0, h };
    }
    this.pendingCount++;
    // fall back to any other bucket for the same text while the right one is pending
    for (const [k, e] of this.textures) {
      if (k.startsWith(base)) {
        e.lastUsed = this.frameNo;
        return { entry: e, y0, h };
      }
    }
    return null;
  }

/**
   * Text that would need a texture bigger than the GPU limit at this zoom (it would blur): drawn as
   * 512 px tiles at full resolution, only those over the view (plus a margin). Null when the text
   * fits one texture, or while some tile is still pending (the capped texture stands in meanwhile).
   */
  private textTiles(cam: FrameInput['camera'], item: RenderItem, pi: number, p: TextPrimitive, scale: number): { entry: TexEntry; x: number; y: number; w: number; h: number }[] | null {
    const layout = layoutText(p.text, p.style, p.w, p.h);
    const fs = p.style.fontSize;
    const first = layout.lines[0];
    const last = layout.lines[layout.lines.length - 1];
    const y0 = Math.min(0, first ? first.y - fs : 0);
    const y1 = Math.max(p.h, last ? last.y + fs * 0.35 : p.h);
    const w = p.w;
    const h = y1 - y0;
    const pow2 = Math.pow(2, Math.ceil(Math.log2(Math.max(scale, 1e-6))));
    if (w * pow2 <= this.maxTex && h * pow2 <= this.maxTex) return null;
    this.tiledText = true;
    // tiles follow the real scale in quarter-octave steps (a power of two would waste up to 4x memory)
    const bucket = Math.pow(2, Math.ceil(Math.log2(scale) * 4) / 4);
    // the view in the primitive's local coordinates (object top-left origin, rotation undone)
    const o = item.obj;
    const v = this.tileView ?? viewRect(cam, 0.25);
    const c = Math.cos(o.rotation);
    const sn = Math.sin(o.rotation);
    const ox = o.x + o.w / 2;
    const oy = o.y + o.h / 2;
    let lx0 = Infinity;
    let ly0 = Infinity;
    let lx1 = -Infinity;
    let ly1 = -Infinity;
    for (const [wx, wy] of [[v.x0, v.y0], [v.x1, v.y0], [v.x1, v.y1], [v.x0, v.y1]]) {
      const dx = wx - ox;
      const dy = wy - oy;
      const lx = dx * c + dy * sn + o.w / 2;
      const ly = -dx * sn + dy * c + o.h / 2;
      lx0 = Math.min(lx0, lx);
      lx1 = Math.max(lx1, lx);
      ly0 = Math.min(ly0, ly);
      ly1 = Math.max(ly1, ly);
    }
    const T = 512;
    const pwAll = Math.ceil(w * bucket);
    const phAll = Math.ceil(h * bucket);
    const ix0 = Math.max(0, Math.floor(((lx0 - p.x) * bucket) / T));
    const ix1 = Math.min(Math.ceil(pwAll / T) - 1, Math.floor(((lx1 - p.x) * bucket) / T));
    const iy0 = Math.max(0, Math.floor(((ly0 - p.y - y0) * bucket) / T));
    const iy1 = Math.min(Math.ceil(phAll / T) - 1, Math.floor(((ly1 - p.y - y0) * bucket) / T));
    const out: { entry: TexEntry; x: number; y: number; w: number; h: number }[] = [];
    if (ix1 < ix0 || iy1 < iy0) return out; // off screen
    if ((ix1 - ix0 + 1) * (iy1 - iy0 + 1) > 160) return null;
    const base = 'tt:' + textKey(p) + ':' + bucket + ':';
    let missing = false;
    for (let iy = iy0; iy <= iy1; iy++) {
      for (let ix = ix0; ix <= ix1; ix++) {
        const key = base + ix + ':' + iy;
        const pw = Math.min(T, pwAll - ix * T);
        const ph = Math.min(T, phAll - iy * T);
        let e = this.textures.get(key);
        if (!e && this.uploadsLeft > 0) {
          this.uploadsLeft--;
          const tex = this.renderTextTile(p, layout, bucket, -ix * T, -y0 * bucket - iy * T, pw, ph);
          if (tex) e = this.addTexture(key, tex, pw, ph);
        }
        if (!e) {
          missing = true;
          continue;
        }
        e.lastUsed = this.frameNo;
        out.push({ entry: e, x: p.x + (ix * T) / bucket, y: p.y + y0 + (iy * T) / bucket, w: pw / bucket, h: ph / bucket });
      }
    }
    if (missing) {
      this.pendingCount++;
      return null;
    }
    return out;
  }

  private renderTextTile(p: TextPrimitive, layout: TextLayout, bucket: number, dx: number, dy: number, pw: number, ph: number): WebGLTexture | null {
    if (!this.textCanvas) this.textCanvas = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(pw, ph) : document.createElement('canvas');
    const cv = this.textCanvas;
    cv.width = pw;
    cv.height = ph;
    const ctx = cv.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
    if (!ctx) return null;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, pw, ph);
    // glyphs at their pixel size: browsers do not draw huge world-size fonts shrunk by a transform
    ctx.setTransform(1, 0, 0, 1, dx, dy);
    ctx.font = fontString(p.style, p.style.fontSize * bucket);
    ctx.fillStyle = cssRgba(p.style.color);
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    for (const l of layout.lines) if (l.text) ctx.fillText(l.text, l.x * bucket, l.y * bucket);
    return this.uploadTexture(cv as unknown as TexImageSource, true);
  }

  private renderTextTexture(p: TextPrimitive, layout: TextLayout, w: number, y0: number, h: number, bucket: number): { tex: WebGLTexture; pw: number; ph: number } | null {
    const pw = Math.max(1, Math.ceil(w * bucket));
    const ph = Math.max(1, Math.ceil(h * bucket));
    if (!this.textCanvas) {
      this.textCanvas = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(pw, ph) : document.createElement('canvas');
    }
    const cv = this.textCanvas;
    cv.width = pw;
    cv.height = ph;
    const ctx = cv.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
    if (!ctx) return null;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, pw, ph);
    // glyphs at their pixel size: browsers do not draw huge world-size fonts shrunk by a transform
    const sx = pw / w;
    const sy = ph / h;
    ctx.setTransform(1, 0, 0, sy / sx, 0, -y0 * sy);
    ctx.font = fontString(p.style, p.style.fontSize * sx);
    ctx.fillStyle = cssRgba(p.style.color);
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    for (const l of layout.lines) if (l.text) ctx.fillText(l.text, l.x * sx, l.y * sx);
    // canvas pixels are straight alpha → premultiply on upload
    return { tex: this.uploadTexture(cv as unknown as TexImageSource, true), pw, ph };
  }

  private imageTexture(frame: FrameInput, src: string): TexEntry | null {
    const key = 'i:' + src;
    const hit = this.textures.get(key);
    if (hit) {
      hit.lastUsed = this.frameNo;
      return hit;
    }
    const img = frame.images.get(src);
    if (!img) return null;
    const w = (img as { width: number }).width;
    const h = (img as { height: number }).height;
    if (!w || !h) return null;
    return this.addTexture(key, this.uploadTexture(img as TexImageSource, true), w, h);
  }

  private rasterTexture(key: string, bitmap: ImageBitmap | null): TexEntry | null {
    const k = 'r:' + key;
    const hit = this.textures.get(k);
    if (hit) {
      hit.lastUsed = this.frameNo;
      return hit;
    }
    if (!bitmap) return null;
    return this.addTexture(k, this.uploadTexture(bitmap, true), bitmap.width, bitmap.height);
  }

  private evictTextures(): void {
    if (this.texBytes <= this.texBudget) return;
    const entries = [...this.textures.entries()].filter(([, e]) => e.lastUsed < this.frameNo).sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    for (const [k, e] of entries) {
      if (this.texBytes <= this.texBudget * 0.8) break;
      this.gl.deleteTexture(e.tex);
      this.textures.delete(k);
      this.texBytes -= e.bytes;
    }
  }
}

/**
 * Identity of a drawn text: what it says and how, not whose it is. The same text keeps its texture
 * when its object moves or is edited elsewhere (a new revision), so moving a block with many text
 * pieces does not redraw them all; identical texts of different objects share one texture.
 */
function textKey(p: TextPrimitive): string {
  const src = p.text + '' + JSON.stringify(p.style) + '' + Math.round(p.w * 1000) + ',' + Math.round(p.h * 1000);
  // FNV-1a over the text and its look; the length guards against the rare collision
  let h = 0x811c9dc5;
  for (let i = 0; i < src.length; i++) {
    h ^= src.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return textEpoch() + ':' + (h >>> 0).toString(36) + ':' + src.length;
}

/** The camera's view in world units, grown by `margin` × its size on every side. */
function viewRect(cam: FrameInput['camera'], margin: number): { x0: number; y0: number; x1: number; y1: number } {
  const hw = cam.width / 2 / cam.zoom;
  const hh = cam.height / 2 / cam.zoom;
  return { x0: cam.cx - hw * (1 + 2 * margin), y0: cam.cy - hh * (1 + 2 * margin), x1: cam.cx + hw * (1 + 2 * margin), y1: cam.cy + hh * (1 + 2 * margin) };
}

function putColor(d: Float32Array, k: number, c: readonly number[] | null, alpha: number): number {
  if (!c) {
    d[k] = d[k + 1] = d[k + 2] = d[k + 3] = 0;
    return k + 4;
  }
  const a = c[3] * alpha;
  d[k] = c[0] * a;
  d[k + 1] = c[1] * a;
  d[k + 2] = c[2] * a;
  d[k + 3] = a;
  return k + 4;
}

function fillKindCode(f: Fill): number {
  switch (f.kind) {
    case 'none':
      return 0;
    case 'solid':
      return 1;
    case 'lines':
      return 2;
    case 'grid':
      return 3;
  }
}

function cssRgba(c: string): string {
  const [r, g, b, a] = parseColor(c);
  return `rgba(${Math.round(r * 255)},${Math.round(g * 255)},${Math.round(b * 255)},${a})`;
}

function signature(items: RenderItem[]): string {
  // cheap rolling hash of (id, rev) pairs
  let h = items.length;
  for (const it of items) {
    h = (h * 31 + it.rev) | 0;
    for (let i = it.id.length - 4; i < it.id.length; i++) h = (h * 31 + it.id.charCodeAt(i)) | 0;
  }
  return items.length + ':' + h;
}
